import React, { useCallback, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { Box, Button, Divider, Slide, Typography } from '@mui/material';
import GridItem from '@jumbo/components/GridItem';
import GridContainer from '@jumbo/components/GridContainer';
import CmtCard from '@coremat/CmtCard';
import CmtCardContent from '@coremat/CmtCard/CmtCardContent';
import CustomAutocomplete from '@acnb/CustomComponents/CustomAutocomplete/CustomAutocomplete';
import { getHighlightParts } from 'services/Utilities/NewHighlights';
import Currency from 'react-currency-formatter';
import { useLanguageReference } from 'services/Hooks/useLanguageReference';
import { useMerchandisingPrograms } from 'services/Data/MerchandisingProgramServices';
import useImage from 'services/Hooks/useImage';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';
import DeleteModal from '@acnb/CustomComponents/_Modals/DeleteModal';
import ImagesSlideView from 'Components/ImagesSlideView';
import {
  useActiveMerchandisingProgramQuote,
  useDeleteMerchandisingProgramQuoteItem,
  useMerchandisingProgramQuoteSortedSeason,
  usePostMerchandisingProgramQuoteItem,
} from 'services/Data/MerchandisingProgramQuoteService';
import { getMerchandisingProgramItemSubLabel, getMerchandisingProgramVisitTimeLabel } from 'services/Utilities/Utilities';
import MerchandisingProgramQuoteItemSubCategoryEnum, {
  MerchandisingProgramQuoteItemSubCategoryEnumValue,
} from 'enums/MerchandisingProgramQuoteItemSubCategoryEnum';
import MerchandisingProgramSeasonCategoryEnum from 'enums/MerchandisingProgramSeasonCategoryEnum';
import useTranslateEnum from 'services/Hooks/useTranslateEnum';
import { useDealer } from 'services/Data/DealerServices';
import { MerchandisingProgramAvailabilityEnumValue } from 'enums/MerchandisingProgramAvailabilityEnum';
import { useMerchandisingProgramCategories } from 'services/Data/MerchandisingProgramCategoryService';
import { getLocalizedCategoryLabel, getMerchandisingCategoryKind } from 'services/Utilities/categoryHelpers';

const useStyles = makeStyles(theme => ({
  mainButton: {
    cursor: 'pointer',
    '&:hover': {
      boxShadow: '0 0 1rem rgba(255, 222, 0, 0.9)',
    },
  },
  guideLink: {
    color: 'rgba(51, 51, 51, 0.8)',
    textDecoration: 'underline',
    '&:hover': {
      color: 'rgba(27, 27, 27, 0.8)',
      textDecoration: 'underline',
    },
  },
}));

const MerchandisingProgramQuote = () => {
  const classes = useStyles();

  let hist = useHistory();

  const [itemToDelete, setItemToDelete] = useState();

  const { t } = useTranslation();
  const { translateEnum } = useTranslateEnum();
  const { labelReference } = useLanguageReference();
  const accountReducer = useSelector(state => state.AccountReducer);
  const isFrench = useMemo(() => t('locale') === 'fr', [t]);

  const { data: sortedMerchandisingProgramSeasonEnums } = useMerchandisingProgramQuoteSortedSeason();
  const { data: merchandisingPrograms, isLoading: gettingMerchandisingProgram } = useMerchandisingPrograms();
  const { data: merchandisingProgramCategories } = useMerchandisingProgramCategories();
  const { data: merchandisingProgramQuote } = useActiveMerchandisingProgramQuote(accountReducer.isLoggedIn);
  const { getMerchandisingProgramImage } = useImage();
  const {
    mutateAsync: addMerchandisingProgramQuoteItem,
    isPending: addingMerchandisingProgramQuoteItem,
  } = usePostMerchandisingProgramQuoteItem(accountReducer.isDemo);
  const { mutate: deleteMerchandisingProgramQuoteItem } = useDeleteMerchandisingProgramQuoteItem();
  const { data: dealer } = useDealer();

  const [temporaryItem, setTemporaryItem] = useState(null);

  const selectedMerchandisingProgram = useMemo(
    () => merchandisingPrograms?.find(es => es.id === temporaryItem?.merchandisingProgramId),
    [merchandisingPrograms, temporaryItem?.merchandisingProgramId],
  );

  const alreadyInQuoteMerchandisingProgramIds = useMemo(
    () => merchandisingProgramQuote?.quoteItems?.map(i => i.merchandisingProgram_Id) || [],
    [merchandisingProgramQuote?.quoteItems],
  );

  const alreadyInQuoteMerchandisingProgramCategoryIds = useMemo(
    () => merchandisingProgramQuote?.quoteItems?.map(i => i.merchandisingProgramCategory_Id).filter(Boolean) || [],
    [merchandisingProgramQuote?.quoteItems],
  );

  const isUsd = useMemo(() => dealer?.currencyName === 'USD', [dealer?.currencyName]);
  const commonMerchandisingProgramsEnum = useMemo(
    () =>
      merchandisingPrograms?.filter(
        mp => mp.merchandisingProgramAvailabilityEnum === MerchandisingProgramAvailabilityEnumValue.All,
      ) || [],
    [merchandisingPrograms],
  );
  const usdMerchandisingProgramsEnum = useMemo(
    () =>
      merchandisingPrograms?.filter(
        mp => mp.merchandisingProgramAvailabilityEnum === MerchandisingProgramAvailabilityEnumValue.Usa,
      ) || [],
    [merchandisingPrograms],
  );
  const canMerchandisingProgramsEnum = useMemo(
    () =>
      merchandisingPrograms?.filter(
        mp => mp.merchandisingProgramAvailabilityEnum === MerchandisingProgramAvailabilityEnumValue.Can,
      ) || [],
    [merchandisingPrograms],
  );

  const filteredMerchandisingPrograms = useMemo(() => {
    let commonMerchandising = [...(commonMerchandisingProgramsEnum || [])];

    if (isUsd) {
      commonMerchandising = [...commonMerchandising, ...usdMerchandisingProgramsEnum];
    } else {
      commonMerchandising = [...commonMerchandising, ...canMerchandisingProgramsEnum];
    }

    return commonMerchandising;
  }, [canMerchandisingProgramsEnum, commonMerchandisingProgramsEnum, isUsd, usdMerchandisingProgramsEnum]);

  const sortedMerchandisingPrograms = useMemo(
    () =>
      filteredMerchandisingPrograms
        ?.filter(es => !alreadyInQuoteMerchandisingProgramIds.includes(es.id))
        .sort((a, b) => a.order - b.order) || [],
    [alreadyInQuoteMerchandisingProgramIds, filteredMerchandisingPrograms],
  );

  const sortedMerchandisingProgramSubCategoryEmums = useMemo(
    () =>
      MerchandisingProgramQuoteItemSubCategoryEnum.filter(
        e =>
          e.value !== MerchandisingProgramQuoteItemSubCategoryEnumValue.Quarterly &&
          e.value !== MerchandisingProgramQuoteItemSubCategoryEnumValue.Once,
      ),
    [],
  );

  const merchandisingProgramCategoryById = useMemo(
    () => new Map((merchandisingProgramCategories || []).map(category => [category.id, category])),
    [merchandisingProgramCategories],
  );

  const availableMerchandisingProgramCategories = useMemo(() => {
    const sortedCategoryIds = [
      ...new Set(
        (sortedMerchandisingPrograms || []).map(program => program.merchandisingProgramCategory_Id).filter(Boolean),
      ),
    ];

    return sortedCategoryIds
      .filter(
        categoryId =>
          alreadyInQuoteMerchandisingProgramCategoryIds.length === 0 ||
          alreadyInQuoteMerchandisingProgramCategoryIds.includes(categoryId),
      )
      .map(categoryId => merchandisingProgramCategoryById.get(categoryId) || { id: categoryId });
  }, [alreadyInQuoteMerchandisingProgramCategoryIds, merchandisingProgramCategoryById, sortedMerchandisingPrograms]);

  const selectedMerchandisingProgramCategory = useMemo(
    () =>
      availableMerchandisingProgramCategories.find(
        category => category.id === temporaryItem?.merchandisingProgramCategoryId,
      ) || merchandisingProgramCategoryById.get(temporaryItem?.merchandisingProgramCategoryId),
    [
      availableMerchandisingProgramCategories,
      merchandisingProgramCategoryById,
      temporaryItem?.merchandisingProgramCategoryId,
    ],
  );

  const selectedMerchandisingProgramCategoryKind = useMemo(
    () => getMerchandisingCategoryKind(selectedMerchandisingProgramCategory),
    [selectedMerchandisingProgramCategory],
  );

  const categorySubCategoryByCategoryId = useMemo(() => {
    const map = new Map();

    (merchandisingProgramQuote?.quoteItems || []).forEach(item => {
      if (item?.merchandisingProgramCategory_Id && item?.merchandisingProgramQuoteItemSubCategoryEnum !== undefined) {
        map.set(item.merchandisingProgramCategory_Id, item.merchandisingProgramQuoteItemSubCategoryEnum);
      }
    });

    return map;
  }, [merchandisingProgramQuote?.quoteItems]);

  const handleAddMerchandisingProgram = useCallback(
    _ => {
      const merchandisingProgramQuoteItem = {
        id: uuidv4(),
        merchandisingProgram_Id: temporaryItem?.merchandisingProgramId,
        unitPrice: selectedMerchandisingProgram?.price,
        subTotal: selectedMerchandisingProgram?.price * temporaryItem?.quantity,
        currencyName: selectedMerchandisingProgram?.currencyName,
        merchandisingProgramCategory_Id: temporaryItem?.merchandisingProgramCategoryId,
        quantity: temporaryItem?.quantity,
        visitLength: selectedMerchandisingProgram?.visitLength,
        merchandisingProgramVisitLengthCategoryEnum:
          selectedMerchandisingProgram?.merchandisingProgramVisitLengthCategoryEnum,
        merchandisingProgramQuote_Id: merchandisingProgramQuote?.id,
        merchandisingProgramQuoteItemSubCategoryEnum: temporaryItem?.subCategory,
        merchandisingProgramSeasonCategoryEnum: temporaryItem?.seasonCategory,
      };
      addMerchandisingProgramQuoteItem(merchandisingProgramQuoteItem).then(_ => {
        setTemporaryItem({});
      });
    },
    [
      temporaryItem?.merchandisingProgramId,
      temporaryItem?.quantity,
      temporaryItem?.merchandisingProgramCategoryId,
      temporaryItem?.subCategory,
      temporaryItem?.seasonCategory,
      selectedMerchandisingProgram?.price,
      selectedMerchandisingProgram?.currencyName,
      selectedMerchandisingProgram?.visitLength,
      selectedMerchandisingProgram?.merchandisingProgramVisitLengthCategoryEnum,
      merchandisingProgramQuote?.id,
      addMerchandisingProgramQuoteItem,
    ],
  );

  const getQuantity = useCallback(merchandisingSubCategoryValue => {
    switch (merchandisingSubCategoryValue) {
      case MerchandisingProgramQuoteItemSubCategoryEnumValue.Quarterly:
        return 4;

      case MerchandisingProgramQuoteItemSubCategoryEnumValue.BiYearly:
        return 2;

      case MerchandisingProgramQuoteItemSubCategoryEnumValue.Yearly:
      case MerchandisingProgramQuoteItemSubCategoryEnumValue.Once:
      case MerchandisingProgramQuoteItemSubCategoryEnumValue.TrainStaff:
        return 1;

      default:
        return 0;
    }
  }, []);

  const handleCloseDeleteModal = useCallback(() => {
    setItemToDelete(null);
  }, [setItemToDelete]);

  const getSubCategoryEnumLabel = useCallback(
    enumObject => {
      let label = translateEnum(MerchandisingProgramQuoteItemSubCategoryEnum, enumObject.value);

      if (enumObject.value === MerchandisingProgramQuoteItemSubCategoryEnumValue.BiYearly) {
        let seasonLabels = sortedMerchandisingProgramSeasonEnums.map(e =>
          translateEnum(MerchandisingProgramSeasonCategoryEnum, e.value),
        );

        if (seasonLabels.length > 0) {
          label += ` (${seasonLabels.join(', ')})`;
        }
      }

      return label;
    },
    [sortedMerchandisingProgramSeasonEnums, translateEnum],
  );

  const merchandisingProgramSubCategoryOptions = useMemo(() => {
    if (selectedMerchandisingProgramCategoryKind === 'seasonal') {
      return sortedMerchandisingProgramSubCategoryEmums;
    }

    if (selectedMerchandisingProgramCategoryKind === 'unknown') {
      return MerchandisingProgramQuoteItemSubCategoryEnum;
    }

    return [];
  }, [selectedMerchandisingProgramCategoryKind, sortedMerchandisingProgramSubCategoryEmums]);

  return (
    <Slide direction="up" in mountOnEnter unmountOnExit>
      <Box className={classes.pageFull}>
        <DeleteModal
          handleCloseModal={handleCloseDeleteModal}
          item={itemToDelete}
          message={t('deleteItemFromMerchandisingProgramQuoteText', {
            detail:
              itemToDelete &&
              getMerchandisingProgramItemSubLabel(itemToDelete, labelReference, sortedMerchandisingProgramSeasonEnums),
            name: itemToDelete?.name,
          })}
          deleteAction={deleteMerchandisingProgramQuoteItem}
        />

        <GridContainer justifyContent="center" alignItems="center" sx={{ height: '100%' }}>
          <GridItem xs={12} xl={8} lg={7} md={6}>
            <ImagesSlideView
              isLoading={gettingMerchandisingProgram}
              images={selectedMerchandisingProgram?.images}
              getImageUrl={imgId => getMerchandisingProgramImage(selectedMerchandisingProgram?.id, imgId)}
              defaultImage={selectedMerchandisingProgram ? '' : '/images/merchandising-program/merchandising-program.jpg'}
            />
          </GridItem>
          <GridItem xs={12} xl={4} lg={5} md={6}>
            <CmtCard sx={{ height: '100%' }} className={classes.itemCard}>
              <CmtCardContent className={classes.itemCardContent} sx={{ height: '100%' }}>
                <Box display="flex" flexDirection={'column'} style={{ width: '100%', marginBottom: '1vh' }}>
                  <Typography variant="h1">{t('merchandisingProgram')}</Typography>
                  <Typography variant="h5" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
                    {t('quote')}
                  </Typography>
                  <Divider />
                </Box>

                <CustomAutocomplete
                  labelText={t('merchandisingProgramCategory')}
                  hasSkeleton
                  isLoading={false}
                  options={availableMerchandisingProgramCategories}
                  formControlProps={{
                    fullWidth: true,
                  }}
                  error={false}
                  autocompleteProps={{
                    onChange: (event, value) => {
                      if (value?.id !== temporaryItem?.merchandisingProgramCategoryId) {
                        const selectedKind = getMerchandisingCategoryKind(value);
                        const quoteSubCategory = categorySubCategoryByCategoryId.get(value?.id);

                        const nextSubCategory =
                          selectedKind === 'quarterly'
                            ? MerchandisingProgramQuoteItemSubCategoryEnumValue.Quarterly
                            : selectedKind === 'trainStaff'
                            ? MerchandisingProgramQuoteItemSubCategoryEnumValue.Once
                            : quoteSubCategory ?? null;

                        setTemporaryItem({
                          merchandisingProgramCategoryId: value?.id,
                          quantity: nextSubCategory ? getQuantity(nextSubCategory) : null,
                          subCategory: nextSubCategory,
                          seasonCategory: null,
                        });
                      }
                    },
                    isOptionEqualToValue: (merchandisingProgramCategory, value) =>
                      merchandisingProgramCategory.id === value?.id,
                    value: selectedMerchandisingProgramCategory || null,
                    getOptionLabel: merchandisingProgramCategory => {
                      return getLocalizedCategoryLabel(merchandisingProgramCategory, isFrench);
                    },
                    filterOptions: (merchandisingProgramCategories, state) => {
                      var searchTerms = state.inputValue.toLowerCase().split(' ');
                      var filteredDiscountTypes = merchandisingProgramCategories.filter(merchandisingProgramCategory =>
                        searchTerms.every(searchTerm =>
                          getLocalizedCategoryLabel(merchandisingProgramCategory, isFrench)
                            ?.toLowerCase()
                            .includes(searchTerm),
                        ),
                      );

                      return filteredDiscountTypes;
                    },
                    renderOption: (props, merchandisingProgramCategory, { inputValue }) => {
                      const result = getHighlightParts(
                        getLocalizedCategoryLabel(merchandisingProgramCategory, isFrench),
                        inputValue,
                      );

                      return (
                        <div className={classes.autoCompleteItem} {...props}>
                          {result}
                        </div>
                      );
                    },
                  }}
                />

                {temporaryItem &&
                  temporaryItem.merchandisingProgramCategoryId &&
                  (selectedMerchandisingProgramCategoryKind === 'seasonal' ||
                    selectedMerchandisingProgramCategoryKind === 'unknown') && (
                    <CustomAutocomplete
                      labelText={t('merchandisingProgramSubCategory')}
                      disabled={!temporaryItem?.merchandisingProgramCategoryId}
                      hasSkeleton
                      isLoading={gettingMerchandisingProgram}
                      options={merchandisingProgramSubCategoryOptions}
                      formControlProps={{
                        fullWidth: true,
                      }}
                      error={false}
                      autocompleteProps={{
                        onChange: (event, value) => {
                          if (value?.value !== temporaryItem?.subCategory) {
                            setTemporaryItem({
                              merchandisingProgramCategoryId: temporaryItem.merchandisingProgramCategoryId,
                              quantity: getQuantity(value.value),
                              subCategory: value?.value,
                              seasonCategory: null,
                            });
                          }
                        },
                        isOptionEqualToValue: (merchandisingProgramSubCategory, value) =>
                          merchandisingProgramSubCategory.value === value.value,
                        value:
                          MerchandisingProgramQuoteItemSubCategoryEnum?.find(
                            esc => esc.value === temporaryItem?.subCategory,
                          ) || null,
                        getOptionLabel: merchandisingProgramSubCategory => {
                          return getSubCategoryEnumLabel(merchandisingProgramSubCategory);
                        },
                        filterOptions: (merchandisingProgramSubCategories, state) => {
                          var searchTerms = state.inputValue.toLowerCase().split(' ');
                          var filteredDiscountTypes = merchandisingProgramSubCategories.filter(
                            merchandisingProgramSubCategory =>
                              searchTerms.every(searchTerm =>
                                getSubCategoryEnumLabel(merchandisingProgramSubCategory)
                                  ?.toLowerCase()
                                  .includes(searchTerm),
                              ),
                          );

                          return filteredDiscountTypes;
                        },
                        renderOption: (props, merchandisingProgramSubCategory, { inputValue }) => {
                          const result = getHighlightParts(
                            getSubCategoryEnumLabel(merchandisingProgramSubCategory),
                            inputValue,
                          );

                          return (
                            <div className={classes.autoCompleteItem} {...props}>
                              {result}
                            </div>
                          );
                        },
                      }}
                    />
                  )}

                {temporaryItem &&
                  temporaryItem.subCategory &&
                  temporaryItem.subCategory === MerchandisingProgramQuoteItemSubCategoryEnumValue.Yearly && (
                    <CustomAutocomplete
                      labelText={t('merchandisingProgramSeasonType')}
                      disabled={!temporaryItem?.subCategory}
                      hasSkeleton
                      isLoading={gettingMerchandisingProgram}
                      options={sortedMerchandisingProgramSeasonEnums}
                      formControlProps={{
                        fullWidth: true,
                      }}
                      error={false}
                      autocompleteProps={{
                        onChange: (event, value) => {
                          if (value?.value !== temporaryItem?.seasonCategory) {
                            setTemporaryItem({
                              merchandisingProgramCategoryId: temporaryItem.merchandisingProgramCategoryId,
                              quantity: temporaryItem.quantity,
                              subCategory: temporaryItem.subCategory,
                              seasonCategory: value?.value,
                            });
                          }
                        },
                        isOptionEqualToValue: (merchandisingProgramSeasonCategory, value) =>
                          merchandisingProgramSeasonCategory.value === value.value,
                        value:
                          MerchandisingProgramSeasonCategoryEnum?.find(esc => esc.value === temporaryItem?.seasonCategory) ||
                          null,
                        getOptionLabel: merchandisingProgramSubCategory => {
                          return translateEnum(
                            MerchandisingProgramSeasonCategoryEnum,
                            merchandisingProgramSubCategory.value,
                          );
                        },
                        filterOptions: (merchandisingProgramSubCategories, state) => {
                          var searchTerms = state.inputValue.toLowerCase().split(' ');
                          var filteredDiscountTypes = merchandisingProgramSubCategories.filter(
                            merchandisingProgramSubCategory =>
                              searchTerms.every(searchTerm =>
                                translateEnum(MerchandisingProgramSeasonCategoryEnum, merchandisingProgramSubCategory.value)
                                  ?.toLowerCase()
                                  .includes(searchTerm),
                              ),
                          );

                          return filteredDiscountTypes;
                        },
                        renderOption: (props, merchandisingProgramSubCategory, { inputValue }) => {
                          const result = getHighlightParts(
                            translateEnum(MerchandisingProgramSeasonCategoryEnum, merchandisingProgramSubCategory.value),
                            inputValue,
                          );

                          return (
                            <div className={classes.autoCompleteItem} {...props}>
                              {result}
                            </div>
                          );
                        },
                      }}
                    />
                  )}

                <CustomAutocomplete
                  labelText={t('merchandisingProgramVisitLength')}
                  disabled={
                    !temporaryItem?.merchandisingProgramCategoryId ||
                    ((selectedMerchandisingProgramCategoryKind === 'seasonal' ||
                      selectedMerchandisingProgramCategoryKind === 'unknown') &&
                      !temporaryItem.subCategory)
                  }
                  hasSkeleton
                  isLoading={gettingMerchandisingProgram}
                  options={
                    sortedMerchandisingPrograms?.filter(
                      es => es.merchandisingProgramCategory_Id === temporaryItem?.merchandisingProgramCategoryId,
                    ) || []
                  }
                  formControlProps={{
                    fullWidth: true,
                  }}
                  error={false}
                  autocompleteProps={{
                    onChange: (event, value) => {
                      if (value?.id !== temporaryItem?.merchandisingProgramId) {
                        setTemporaryItem({
                          merchandisingProgramCategoryId: temporaryItem.merchandisingProgramCategoryId,
                          quantity: temporaryItem.quantity,
                          subCategory: temporaryItem.subCategory,
                          seasonCategory: temporaryItem.seasonCategory,
                          merchandisingProgramId: value?.id,
                        });
                      }
                    },
                    isOptionEqualToValue: (panelType, value) => panelType.id === value,
                    value: merchandisingPrograms?.find(esc => esc.id === temporaryItem?.merchandisingProgramId) || null,
                    getOptionLabel: merchandisingProgram => {
                      return (
                        (merchandisingProgram &&
                          t('formatMerchandisingProgram', {
                            merchandisingProgramName: merchandisingProgram.name,
                            visitLengthName: getMerchandisingProgramVisitTimeLabel(
                              merchandisingProgram.visitLength,
                              merchandisingProgram.merchandisingProgramVisitLengthCategoryEnum,
                              labelReference,
                            ),
                            price: merchandisingProgram.price,
                            formatParams: {
                              price: { currency: 'USD', locale: 'en-US' },
                            },
                          })) ||
                        (merchandisingProgram && merchandisingProgram.name) ||
                        ''
                      );
                    },
                    filterOptions: (merchandisingPrograms, state) => {
                      var searchTerms = state.inputValue.toLowerCase().split(' ');
                      var filteredDiscountTypes = merchandisingPrograms.filter(merchandisingProgram =>
                        searchTerms.every(searchTerm =>
                          t('formatMerchandisingProgram', {
                            merchandisingProgramName: merchandisingProgram.name,
                            visitLengthName: getMerchandisingProgramVisitTimeLabel(
                              merchandisingProgram.visitLength,
                              merchandisingProgram.merchandisingProgramVisitLengthCategoryEnum,
                              labelReference,
                            ),
                            price: merchandisingProgram.price,
                            formatParams: {
                              price: { currency: 'USD', locale: 'en-US' },
                            },
                          })
                            ?.toLowerCase()
                            .includes(searchTerm),
                        ),
                      );

                      return filteredDiscountTypes;
                    },
                    renderOption: (props, merchandisingProgram, { inputValue }) => {
                      const result = getHighlightParts(
                        t('formatMerchandisingProgram', {
                          merchandisingProgramName: merchandisingProgram.name,
                          visitLengthName: getMerchandisingProgramVisitTimeLabel(
                            merchandisingProgram.visitLength,
                            merchandisingProgram.merchandisingProgramVisitLengthCategoryEnum,
                            labelReference,
                          ),
                          price: merchandisingProgram.price,
                          formatParams: {
                            price: { currency: 'USD', locale: 'en-US' },
                          },
                        }),
                        inputValue,
                      );

                      return (
                        <div className={classes.autoCompleteItem} {...props}>
                          {result}
                        </div>
                      );
                    },
                  }}
                />

                <Box display="flex" flexDirection={'column'}>
                  {selectedMerchandisingProgram && <Divider sx={{ my: 3 }} />}
                  <Box
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      marginTop: '1vh',
                      flexDirection: 'column',
                    }}>
                    {selectedMerchandisingProgram && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          width: '100%',
                        }}>
                        <Box>
                          <Typography variant="h2">{t('price')}</Typography>
                        </Box>
                        <Typography fontSize={25} fontWeight="bold">
                          <Currency
                            quantity={temporaryItem?.quantity * selectedMerchandisingProgram?.price}
                            currency={merchandisingProgramQuote?.currencyName}
                          />{' '}
                          {t('merchandisingProgramSubTotalFormated', {
                            count: temporaryItem?.quantity,
                          })}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {selectedMerchandisingProgram && <Divider sx={{ my: 2 }} />}

                  <Box
                    sx={{
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      display: 'flex',
                      pt: 0,
                    }}>
                    <Button
                      key={uuidv4()}
                      sx={{ borderRadius: 0 }}
                      disabled={
                        addingMerchandisingProgramQuoteItem ||
                        !temporaryItem?.merchandisingProgramCategoryId ||
                        !temporaryItem.subCategory ||
                        !temporaryItem?.merchandisingProgramId
                      }
                      variant={'contained'}
                      color={'secondary'}
                      onClick={handleAddMerchandisingProgram}>
                      <Typography
                        sx={{
                          color: 'black !important',
                          fontSize: 16,
                          fontWeight: 700,
                          paddingRight: 10,
                          paddingLeft: 10,
                          paddingTop: 2,
                          paddingBottom: 2,
                        }}>
                        {t('add')}
                      </Typography>
                    </Button>
                  </Box>
                </Box>
              </CmtCardContent>
            </CmtCard>

            <CmtCard sx={{ mt: 2, minHeight: '20vh' }}>
              <CmtCardContent>
                {selectedMerchandisingProgram ? (
                  <Box>
                    <Typography variant="h2">{selectedMerchandisingProgram.name}</Typography>
                    <Typography variant="h6">
                      {getMerchandisingProgramVisitTimeLabel(
                        selectedMerchandisingProgram.visitLength,
                        selectedMerchandisingProgram.merchandisingProgramVisitLengthCategoryEnum,

                        labelReference,
                      )}
                    </Typography>
                    <Typography sx={{ mt: 5 }} variant="body1">
                      <span
                        dangerouslySetInnerHTML={{
                          __html: selectedMerchandisingProgram.description,
                        }}
                      />
                    </Typography>

                    {selectedMerchandisingProgram.warning && (
                      <Typography sx={{ mt: 2 }} variant="h6">
                        {selectedMerchandisingProgram.warning}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  t('no_visit_type_selected')
                )}
              </CmtCardContent>
            </CmtCard>
          </GridItem>
          <GridItem xs={12} textAlign="right">
            <Button
              sx={{ borderRadius: 0 }}
              disabled={(merchandisingProgramQuote?.quoteItems?.length || 0) <= 0}
              variant={'contained'}
              color={'secondary'}
              onClick={() => {
                hist.push('/dealer/home-page/merchandising-program/check-out');
              }}>
              <Typography
                sx={{
                  color: 'black !important',
                  fontSize: 16,
                  fontWeight: 700,
                  paddingRight: 10,
                  paddingLeft: 10,
                  paddingTop: 2,
                  paddingBottom: 2,
                }}>
                {t('proceedCheckoutMerchandisingProgramQuote')}
              </Typography>
            </Button>
          </GridItem>
        </GridContainer>
      </Box>
    </Slide>
  );
};

export default MerchandisingProgramQuote;
